import { useFormatMenuItem } from "hooks/useFormatMenuItem";
import {
    NewsRoutes,
    Overlay,
    PanelMenuItem,
    RoleType,
    useRouteSearch,
    useTitle,
} from "modules/common";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoutes, RouteParams } from "routing";
import { RootState } from "store";

const routes = [NewsRoutes.News, NewsRoutes.Advice, NewsRoutes.Advisers];

const getMenuItems = (userRole?: RoleType): PanelMenuItem[] => [
    { label: "Vijesti", link: NewsRoutes.News },
    { label: "Savjeti", link: NewsRoutes.Advice },
    {
        label:
            userRole === RoleType.Farmer || userRole === RoleType.Citizen
                ? "Moj Savjetnik"
                : "Savjetnici",
        link: NewsRoutes.Advisers,
        fullScreen: true,
    },
];

export const News: React.FC = () => {
    useTitle("Vijesti - eSavjetnik");

    const userRole = useSelector((state: RootState) => state.signIn?.user?.role.value);
    const { search, pathname } = useLocation();
    const history = useHistory();

    const menuItems = useMemo(() => getMenuItems(userRole), [userRole]);
    const [menuItem, setMenuItem] = useState(menuItems.find(mi => !mi.isDisabled));
    const [parameters, setParameters] = useState<{ adviceId?: string; newsId?: string }>({});

    const { searchElement } = useRouteSearch({
        routes,
        menuItem,
        inputProps: {
            placeholder:
                menuItem?.link === NewsRoutes.Advisers
                    ? "Pretražite savjetnike"
                    : "Pretraži po naslovu, sadržaju i kategoriji.",
        },
    });

    useEffect(() => {
        const params = new URLSearchParams(search);
        const newsId = params.get(RouteParams.NewsId);
        const adviceId = params.get(RouteParams.AdviceId);

        const paramRecord: { adviceId?: string; newsId?: string } = {
            ...(adviceId && { adviceId }),
            ...(newsId && { newsId }),
        };

        setParameters(paramRecord);
    }, [search]);

    useEffect(() => {
        const routeMap: Record<string, string> = {
            [NewsRoutes.Advice]: NewsRoutes.Advice,
            [NewsRoutes.Advisers]: NewsRoutes.Advisers,
            [NewsRoutes.News]: NewsRoutes.News,
        };

        const targetRoute = routeMap[pathname];

        if (targetRoute) {
            setMenuItem(menuItems.find(mi => mi.link === targetRoute));
        } else if (pathname === AppRoutes.News) {
            const params = new URLSearchParams(search);
            const adviceId = params.get(RouteParams.AdviceId);
            const newsId = params.get(RouteParams.NewsId);

            if (adviceId) {
                setMenuItem(menuItems.find(mi => mi.link === NewsRoutes.Advice));
                history.replace(`${NewsRoutes.Advice}${search}`);
            } else if (newsId) {
                setMenuItem(menuItems.find(mi => mi.link === NewsRoutes.News));
                history.replace(`${NewsRoutes.News}${search}`);
            } else {
                setMenuItem(menuItems.find(mi => mi.link === NewsRoutes.News));
                history.push(NewsRoutes.News);
            }
        }
    }, [pathname, menuItems, history, search]);

    useEffect(() => {
        if (parameters.adviceId) {
            setMenuItem(menuItems.find(mi => mi.link === NewsRoutes.Advice));
        } else if (parameters.newsId) {
            setMenuItem(menuItems.find(mi => mi.link === NewsRoutes.News));
        }
    }, [parameters, menuItems]);

    const isDynamicHeight = menuItem?.link !== NewsRoutes.Advisers;
    const formatMenuItem = useFormatMenuItem(menuItem?.label);

    return (
        <Overlay
            key={`${pathname}-${menuItem?.link}`}
            containerClass={formatMenuItem}
            backButton
            search={searchElement}
            fullScreenInitially={menuItem?.fullScreen ?? false}
            view={{
                title: "Vijesti",
                source: mi => `${mi?.link ?? ""}${search}`,
                dynamicHeight: isDynamicHeight,
                parameters: parameters,
            }}
            menuItems={menuItems}
            selectedMenuItem={menuItem}
            onMenuItemChanged={mi => {
                history.push(mi.link);
            }}
        />
    );
};
